import React, { useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { generatePath, useNavigate } from 'react-router-dom'
import Button from 'components/button'
import Modal, { ModalProps } from 'components/modal'
import { useProject } from 'hooks/useProject'
import { useAuth } from 'hooks/auth'
import api from 'helpers/api'
import { TABLE, INTEGRATIONS } from 'routes'
import { useApplicationStore } from 'hooks/application'
import Checkbox from 'components/checkbox'
import Select from 'components/select'
import Integration from 'components/integrations'
import { syncIntegration } from 'components/spreadsheet/components/modal/views/integration/helpers/sync'
import {
  IntegrationProviders,
  IntegrationSyncResourceTypes,
  SYNC_CONFIGURATION_RESTRICTED_MESSAGE
} from 'app-constants'
import { Plus } from 'components/icons'
import { ITable, IIntegrationCatalogue, IntegrationButtonProps, IIntegrationSyncResourceTypes } from 'types'
import { capitaliseEachWord } from 'helpers/utils'
import { isAccountConnected } from 'helpers/integration'
import newtableImg from 'assets/images/newtable.webp'
import csvtableImg from 'assets/images/csvtable.webp'
import externaltableImg from 'assets/images/externaltable.webp'
import Input from 'components/input'
import Tooltip from 'components/tooltip'
import useFocusWhenOpen from 'hooks/useFocusWhenOpen'
import ProgressBar from 'components/progressBar'
import { deriveCsvColumns, streamCsvRowsToView, ImportProgress, CsvImportError } from './csvImport'

interface NewTableProps extends ModalProps {
  noRedirect?: boolean
  noRedirectFunction?: (table: ITable) => void
  /** The sidebar folder to create the table in; the top level when unset. */
  folderId?: string | null
}

type newTableTypes = 'Upload CSV' | 'Blank Table' | 'External Data Source'

const NewTable: React.FC<NewTableProps> = ({ id, open, setOpen, noRedirect, noRedirectFunction, folderId = null }) => {
  const { displayErrorMessage } = useApplicationStore()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [name, setName] = useState<string>()
  const tableSourceInit: newTableTypes = 'Blank Table'
  const [source, setSource] = useState<newTableTypes>(tableSourceInit)
  const inputFile: any = useRef(null)
  const [forHeadersChecked, setForHeadersChecked] = useState(true)
  const { project } = useProject()
  const canConfigureSync = project.canConfigureSync !== false
  const [creatingTableLoading, setCreatingTableLoading] = useState(false)
  const [importProgress, setImportProgress] = useState<ImportProgress | null>(null)
  const cancelledRef = useRef(false)
  const nameInput = useFocusWhenOpen(open)
  const { setSnackbarMessage } = useApplicationStore()
  const [catalogue, setCatalogue] = useState<IIntegrationCatalogue>({})
  const [loadingCatalogue, setLoadingCatalogue] = useState(false)
  const [catalogueLoaded, setCatalogueLoaded] = useState(false)
  const [selectedProvider, setSelectedProvider] = useState<IntegrationProviders>()
  const [selectedResourceType, setSelectedResourceType] = useState<IntegrationSyncResourceTypes>()

  // Fetch the catalogue lazily — only once the modal is open and the user has actually chosen
  // the external option (it drives whether the data-source dropdowns show). This avoids an
  // extra API call every time the create-table modal mounts for a blank/CSV table.
  useEffect(() => {
    if (!open || source !== 'External Data Source' || catalogueLoaded) {
      return
    }
    setLoadingCatalogue(true)
    api
      .getIntegrationCatalogue()
      .then((res) => {
        setCatalogue(res.data)
        setCatalogueLoaded(true)
      })
      .catch((error) => displayErrorMessage(error))
      .finally(() => setLoadingCatalogue(false))
  }, [open, source, catalogueLoaded, displayErrorMessage])

  // Only offer data sources the user actually has a connected account for (Morta is always
  // available). If there are none, we hide the dropdowns entirely below.
  const providerOptions = Object.entries(catalogue)
    .filter(([provider]) => isAccountConnected(user, provider))
    .map(([provider, info]) => ({ label: info.display, value: provider }))
    .sort((a, b) => a.label.localeCompare(b.label))

  const selectedProviderInfo = selectedProvider ? catalogue[selectedProvider] : undefined
  const resourceOptions = selectedProviderInfo
    ? selectedProviderInfo.types
        .filter((type) => !(selectedProviderInfo.disabledTypes || []).includes(type))
        .map((type) => ({ label: capitaliseEachWord(type.replace(/_/g, ' ')) || type, value: type }))
        .sort((a, b) => a.label.localeCompare(b.label))
    : []

  const handleClose = () => {
    setOpen(false)
  }

  const redirectOrHandOff = (table: ITable) => {
    // Flush loading=false synchronously first: it drives the Modal's navigation blocker, so
    // leaving it set would re-prompt the "stop the import" confirm on the route change below.
    flushSync(() => setCreatingTableLoading(false))
    if (!noRedirect || !noRedirectFunction) {
      navigate(
        generatePath(TABLE, {
          projectId: project.publicId,
          tableId: table.publicId,
          viewId: table.defaultViewId
        })
      )
    } else {
      noRedirectFunction(table)
    }
    handleClose()
  }

  // Creates a blank table and immediately sets up the chosen external data source sync on
  // it, then redirects to (or hands back) the new externally-backed table. Reuses the same
  // sync configuration form and endpoint as the toolbar's "Import Data" flow.
  const createExternalTable = async (props: IntegrationButtonProps) => {
    if (!name?.trim()) {
      setSnackbarMessage({ status: 'error', message: 'Please enter a name for the table.' })
      return
    }
    if (!selectedProvider || !selectedResourceType || creatingTableLoading) {
      return
    }

    setCreatingTableLoading(true)
    let res
    try {
      res = await api.createNewTable({
        projectId: project.publicId,
        name,
        folderId,
        context: { projectId: project.publicId }
      })
    } catch (error) {
      displayErrorMessage(error)
      setCreatingTableLoading(false)
      return
    }

    if (!res || !res.data) {
      setCreatingTableLoading(false)
      return
    }

    const table = res.data
    syncIntegration(
      user,
      selectedProvider,
      props,
      selectedResourceType as IIntegrationSyncResourceTypes,
      // Re-entry is already guarded by creatingTableLoading above.
      false,
      () => {},
      table.publicId,
      () => redirectOrHandOff(table),
      (error) => {
        displayErrorMessage(error)
        setCreatingTableLoading(false)
      },
      false
    )
  }

  // Closing the modal mid-import stops the stream. Rows already created stay in
  // the table (we navigate to it), so the user sees what was imported so far.
  const handleImportCancel = () => {
    cancelledRef.current = true
  }

  // Read at call time: the file input is uncontrolled, so a render-time value could be stale
  const canCreateTable = (): boolean =>
    source !== 'External Data Source' &&
    !!name?.trim() &&
    !(source === 'Upload CSV' && !inputFile.current?.files?.[0]) &&
    !creatingTableLoading

  async function createTable() {
    if (name && canCreateTable()) {
      const file = inputFile.current && inputFile.current.files && inputFile.current.files[0]
      if (source === 'Blank Table') {
        // Blocks a second create (e.g. a double Enter) while this one is in flight
        setCreatingTableLoading(true)
        let res
        try {
          res = await api.createNewTable({
            projectId: project.publicId,
            name,
            folderId,
            context: { projectId: project.publicId }
          })
        } catch (error) {
          setCreatingTableLoading(false)
          displayErrorMessage(error)
          return
        }

        if (res && res.data) {
          redirectOrHandOff(res.data)
        } else {
          setCreatingTableLoading(false)
        }

        return
      }

      if (source === 'Upload CSV') {
        if (file && creatingTableLoading === false) {
          setCreatingTableLoading(true)
          // Create the table from just the CSV's header, then stream the rows in
          // batches, so a large CSV is never POSTed as one multipart request.
          let createdTable: ITable | undefined
          cancelledRef.current = false
          setImportProgress({ current: 0, total: 1 })
          try {
            const columns = await deriveCsvColumns(file, forHeadersChecked)
            const columnNames = columns.map((column) => column.name)
            const res = await api.createNewTable({
              projectId: project.publicId,
              name,
              columns,
              folderId,
              context: { projectId: project.publicId }
            })
            createdTable = res.data
            await streamCsvRowsToView(
              file,
              project.publicId,
              createdTable.defaultViewId,
              columnNames,
              forHeadersChecked,
              (progress) => setImportProgress(progress),
              () => cancelledRef.current
            )
          } catch (error) {
            // Roll back a partially-imported table so a failed import doesn't leave an orphan.
            if (createdTable) {
              await api
                .deleteTable(createdTable.publicId, { context: { projectId: project.publicId } })
                .catch(() => undefined)
            }
            displayErrorMessage(error instanceof CsvImportError ? error.message : error)
            setCreatingTableLoading(false)
            setImportProgress(null)
            return
          }

          setImportProgress(null)
          redirectOrHandOff(createdTable)
        }
      }
    } else {
      if (!name?.trim()) {
        setSnackbarMessage({
          status: 'error',
          message: 'Please enter a name for the table.'
        })
      } else if (source === 'Upload CSV' && !inputFile.current.files[0]) {
        setSnackbarMessage({
          status: 'error',
          message: 'Please select a CSV file to upload.'
        })
      }
    }
  }

  // An external table is set up as a sync, so it follows the organisation's sync restriction. Blocked here, before
  // the empty table would be created, rather than when the sync is refused.
  const externalSourceCard = (
    <div
      data-cy="create-table-external"
      className={`flex flex-col overflow-hidden rounded-lg ${
        source === 'External Data Source' ? 'border-blue' : 'border-divider'
      } border-2 transition-all min-w-[240px] min-h-[400px] md:min-w-0 md:flex-1 md:basis-0 ${
        creatingTableLoading || !canConfigureSync ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:shadow-md'
      }`}
      onClick={() => !creatingTableLoading && canConfigureSync && setSource('External Data Source')}
      aria-disabled={!canConfigureSync || undefined}
    >
      <div className="flex items-center justify-center min-h-[200px] max-h-[200px] bg-light-purple">
        <img
          className="border-divider shadow max-h-[60%] max-w-[60%] object-contain"
          src={externaltableImg}
          alt="Morta External Data Source"
        />
      </div>
      <div className="border-t border-divider flex-1 p-5">
        <div className="font-bold text-lg mb-2.5">Create from External Data Sources</div>
        <div>Connect a data source such as Autodesk, Procore or Asta and populate this table with synced data.</div>
      </div>
    </div>
  )
  // Only a CSV import can be stopped part-way; a blank or external create can't, so leaving one isn't blocked
  const importingCsv = creatingTableLoading && source === 'Upload CSV'

  return (
    <Modal
      id={id}
      open={open}
      setOpen={setOpen}
      title="Create New Table"
      shouldBlockNavigation={importingCsv}
      confirmMessage="Exiting now will stop the import. Only the rows imported so far will be added to the table — the remaining rows will not be imported. Are you sure you want to exit?"
      onClose={importingCsv ? handleImportCancel : undefined}
    >
      <Input
        ref={nameInput}
        className="mb-5"
        data-tour="table-name-input"
        data-cy="table-name"
        required
        disabled={creatingTableLoading}
        placeholder="Enter the name of your new table..."
        onChange={(event) => setName(event.target.value)}
        onKeyDown={(event) => {
          // Enter creates only when the Create button is shown and the form is complete
          if (event.key === 'Enter' && canCreateTable()) createTable()
        }}
      />

      <div className="flex flex-col gap-5 md:flex-row md:items-stretch">
        <div
          data-cy="create-table-blank"
          className={`flex flex-col overflow-hidden rounded-lg ${
            source === 'Blank Table' ? 'border-blue' : 'border-divider'
          } border-2 transition-all min-w-[240px] min-h-[400px] md:min-w-0 md:flex-1 md:basis-0 ${
            creatingTableLoading ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:shadow-md'
          }`}
          onClick={() => !creatingTableLoading && setSource('Blank Table')}
        >
          <div className="flex items-center justify-center min-h-[200px] max-h-[200px] bg-really-light-blue">
            <img
              className="border-divider shadow max-h-[60%] max-w-[60%] object-contain"
              src={newtableImg}
              alt="Morta New Table"
            />
          </div>
          <div className="border-t border-divider flex-1 p-2.5">
            <div className="font-bold text-lg mb-2.5">Create from scratch</div>
            <div>Start with a blank table and add your own columns and rows.</div>
          </div>
        </div>

        <div
          data-cy="create-table-csv"
          className={`flex flex-col overflow-hidden rounded-lg ${
            source === 'Upload CSV' ? 'border-blue' : 'border-divider'
          } border-2 transition-all min-w-[240px] min-h-[400px] md:min-w-0 md:flex-1 md:basis-0 ${
            creatingTableLoading ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:shadow-md'
          }`}
          onClick={() => !creatingTableLoading && setSource('Upload CSV')}
        >
          <div className="flex items-center justify-center min-h-[200px] max-h-[200px] bg-light-green">
            <img
              className="border-divider shadow max-h-[60%] max-w-[60%] object-contain"
              src={csvtableImg}
              alt="Morta CSV Table"
            />
          </div>
          <div className="border-t border-divider flex-1 p-2.5">
            <div className="font-bold text-lg mb-2.5">Create from CSV file</div>
            <div className="mb-5">Upload a CSV file to create a table with your data.</div>
            <input
              className="mb-5 w-full cursor-pointer overflow-hidden rounded-lg border border-divider bg-surface text-sm text-body file:mr-2.5 file:cursor-pointer file:border-0 file:border-r file:border-divider file:bg-lightgrey file:px-4 file:py-2 p-0 file:text-body hover:file:bg-surface-strong/80 disabled:cursor-not-allowed disabled:opacity-60"
              type="file"
              id="file"
              ref={inputFile}
              accept=".csv"
              disabled={creatingTableLoading}
            />
            <Checkbox
              checked={forHeadersChecked}
              disabled={creatingTableLoading}
              onChange={(event) => {
                setForHeadersChecked(event.target.checked)
              }}
            >
              Use first row as column headers
            </Checkbox>
          </div>
        </div>

        {canConfigureSync ? (
          externalSourceCard
        ) : (
          <Tooltip content={SYNC_CONFIGURATION_RESTRICTED_MESSAGE}>{externalSourceCard}</Tooltip>
        )}
      </div>

      {source === 'External Data Source' && canConfigureSync && (
        <div className="mt-5 overflow-hidden rounded-lg border-2 border-blue" data-cy="external-source-panel">
          <div className="p-5">
            {loadingCatalogue ? (
              <div className="text-muted text-sm">Loading data sources…</div>
            ) : providerOptions.length === 0 ? (
              <div className="text-muted text-sm" data-cy="external-no-sources">
                You have no connected data sources.{' '}
                <a className="text-blue underline" href={INTEGRATIONS}>
                  Connect one in your integration settings
                </a>{' '}
                to create a table from an external data source.
              </div>
            ) : (
              <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
                <div className="flex-1">
                  <Select
                    label="Data Source"
                    placeholder="Select a data source..."
                    options={providerOptions}
                    optionsSelected={selectedProvider ? [selectedProvider] : []}
                    onOptionClick={(value) => {
                      setSelectedProvider(value as IntegrationProviders)
                      setSelectedResourceType(undefined)
                    }}
                  />
                </div>
                <div className="flex-1">
                  <Select
                    label="Data to Import"
                    placeholder="Select what to import..."
                    options={resourceOptions}
                    optionsSelected={selectedResourceType ? [selectedResourceType] : []}
                    disabled={!selectedProvider}
                    onOptionClick={(value) => setSelectedResourceType(value as IntegrationSyncResourceTypes)}
                  />
                </div>
              </div>
            )}
          </div>

          {selectedProvider && selectedResourceType && (
            <div className="border-t border-divider p-5" data-cy="external-source-config">
              <Integration
                provider={selectedProvider}
                type={selectedResourceType as IIntegrationSyncResourceTypes}
                buttonLabel="Create New Table"
                buttonLoading={creatingTableLoading}
                buttonFunction={(props) => createExternalTable(props)}
                compact
              />
            </div>
          )}
        </div>
      )}

      {importProgress && importProgress.total > 0 && (
        <div className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm">Importing rows... Please do not refresh or leave the page.</span>
            <span className="text-sm font-bold">
              {Math.round((importProgress.current / importProgress.total) * 100)}%
            </span>
          </div>
          <ProgressBar percentage={(importProgress.current / importProgress.total) * 100} />
        </div>
      )}

      {source !== 'External Data Source' && (
        <div className="flex mt-5">
          <Button
            className="ml-auto text-base"
            data-tour="create-table-submit"
            internalType="accept"
            onClick={() => createTable()}
            data-cy="create-table-button"
            isLoading={creatingTableLoading}
            disabled={creatingTableLoading}
          >
            <Plus className="mr-[5px]" /> Create New Table
          </Button>
        </div>
      )}
    </Modal>
  )
}

export default NewTable
